/**
 * Per-role agent host CRUD + chat history endpoints.
 *
 * Each `(company_id, role)` pair has at most one row in `cloud_agent_hosts`
 * on the hub. The row records which device runs the local Claude CLI for
 * that role, the model the SDK should pass to `--model`, and the permission
 * mode it should run in.
 */

import type { AgentRole, AgentPermissionMode } from "@paperclip-cloud/shared";
import { api } from "../client";

export interface AgentHost {
  id: string;
  company_id: string;
  role: AgentRole;
  /**
   * Which paired device hosts this role. NULL while unconfigured — the hub
   * refuses agent turns until a device is assigned.
   */
  device_id: string | null;
  /** The model id passed to the Claude CLI (--model). */
  model: string;
  permission_mode: AgentPermissionMode;
  enabled: boolean;
  created_at: number;
  updated_at: number;
}

export interface AgentHostPatch {
  device_id?: string | null;
  model?: string;
  permission_mode?: AgentPermissionMode;
  enabled?: boolean;
}

export const agentHostsApi = {
  list: (companyId: string) =>
    api<{ agent_hosts: AgentHost[] }>(
      `/v1/companies/${companyId}/agent-hosts`,
    ),
  update: (companyId: string, role: AgentRole, patch: AgentHostPatch) =>
    api<AgentHost>(`/v1/companies/${companyId}/agent-hosts/${role}`, {
      method: "PATCH",
      body: patch,
    }),
};

/**
 * Persisted chat history for an agent thread. The hub stores both user
 * inputs and the agent's final synthesized text.
 */
export interface AgentMessage {
  id: string;
  company_id: string;
  role: AgentRole;
  /** Who emitted the line. */
  author: "user" | "agent" | "system" | "tool";
  content: string;
  work_order_id: string | null;
  /**
   * When set, this row was folded into a compression-summary message and is
   * hidden from the chat by default. The summary itself is a `system`-author
   * row with `compressed_into_id == null` whose content begins with
   * `[compressed N messages]`.
   */
  compressed_into_id: string | null;
  created_at: number;
}

export const agentMessagesApi = {
  list: (companyId: string, role: AgentRole) =>
    api<{ messages: AgentMessage[] }>(
      `/v1/companies/${companyId}/agent-messages?role=${encodeURIComponent(role)}`,
    ),
  send: (
    companyId: string,
    role: AgentRole,
    content: string,
    workingDir?: string | null,
  ) =>
    api<{ message_id: string }>(`/v1/agents/messages`, {
      method: "POST",
      body: {
        company_id: companyId,
        role,
        content,
        // Optional working-dir hint. The hub may not yet accept this; if it
        // ignores the field that's fine — the request still succeeds.
        ...(workingDir ? { working_dir: workingDir } : {}),
      },
    }),
  /**
   * Fold the role's uncompressed transcript into a single summary. The hub
   * orchestrates a one-shot LLM turn on the role's host (no tools, no chat
   * persona). Blocks for the full LLM turn — UI should show "Compressing…".
   */
  compress: (companyId: string, role: AgentRole) =>
    api<{ summary_message_id: string; rows_compressed: number }>(
      `/v1/companies/${companyId}/agent-messages/compress`,
      { method: "POST", body: { role } },
    ),
};
